// Copyright (c) Bentley Systems
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { AreaModel } from "../Areas/Models/area-model";
import { getQueryParam } from "../Helpers/helper-functions";

type HeaderState = {
  areas: AreaModel[];
  activeAreaId: number | undefined;
  activeNodeId: number | undefined;
  isSideMenuExpanded: boolean | undefined;
  header: string | undefined;
  hideSideMenu: boolean;
  hideMainMenu: boolean;
  sideMenuWidth: number | undefined;
  mainMenuHeight: number | undefined;
};
const isBackToEntryScreenQueryParameter = getQueryParam("backToEntryScreen");

const initialState: HeaderState = {
  areas: [],
  activeAreaId: undefined,
  activeNodeId: undefined,
  isSideMenuExpanded: isBackToEntryScreenQueryParameter ? true : false,
  header: undefined,
  hideSideMenu: false,
  hideMainMenu: false,
  sideMenuWidth: undefined,
  mainMenuHeight: undefined,
};

export const headerSlice = createSlice({
  name: "header",
  initialState,
  reducers: {
    setHeaders: (state, action: PayloadAction<AreaModel[]>) => {
      state.areas = action.payload;
    },
    setHeader: (state, action: PayloadAction<string | undefined>) => {
      state.header = action.payload;
    },
    setActiveAreaId: (state, action: PayloadAction<number | undefined>) => {
      state.activeAreaId = action.payload;
    },
    setActiveNodeId: (state, action: PayloadAction<number | undefined>) => {
      state.activeNodeId = action.payload;
    },
    setIsSideMenuExpanded: (
      state,
      action: PayloadAction<boolean | undefined>,
    ) => {
      state.isSideMenuExpanded = action.payload;
    },
    setSideMenuWidth: (state, action: PayloadAction<number | undefined>) => {
      state.sideMenuWidth = action.payload;
    },
    setHideSideMenu: (state, action: PayloadAction<boolean>) => {
      state.hideSideMenu = action.payload;
    },
    setHideMainMenu: (state, action: PayloadAction<boolean>) => {
      state.hideMainMenu = action.payload;
    },
    setMainMenuHeight: (state, action: PayloadAction<number | undefined>) => {
      state.mainMenuHeight = action.payload;
    },
  },
});

export const {
  setHeaders,
  setHeader,
  setActiveAreaId,
  setActiveNodeId,
  setIsSideMenuExpanded,
  setSideMenuWidth,
  setHideSideMenu,
  setHideMainMenu,
  setMainMenuHeight,
} = headerSlice.actions;

export const selectHeaders = (state: RootState) => state.headers.areas;
export const selectHeader = (state: RootState) => state.headers.header;
export const selectActiveAreaId = (state: RootState) =>
  state.headers.activeAreaId;
export const selectActiveNodeId = (state: RootState) =>
  state.headers.activeNodeId;
export const selectIsSideMenuExpanded = (state: RootState) =>
  state.headers.isSideMenuExpanded;
export const selectSideMenuWidthWithMargin = (state: RootState) =>
  45 + (state.headers.sideMenuWidth ? state.headers.sideMenuWidth : 0);
export const selectSideMenuWidthOnly = (state: RootState) =>
  state.headers.sideMenuWidth ? state.headers.sideMenuWidth : 0;
export const selectHideSideMenu = (state: RootState) =>
  state.headers.hideSideMenu;
export const selectHideMainMenu = (state: RootState) =>
  state.headers.hideMainMenu;
export const selectMainMenuHeightWithMargin = (state: RootState) =>
  45 + (state.headers.mainMenuHeight ? state.headers.mainMenuHeight : 0);
export const selectMainMenuHeightOnly = (state: RootState) =>
  state.headers.mainMenuHeight ? state.headers.mainMenuHeight : 0;

export default headerSlice.reducer;
